'use client';

import { motion } from 'motion/react';
import Image from 'next/image';
import { useRef } from 'react';
import { twJoin } from 'tailwind-merge';

import { Button } from '@/components/Button';
import { useResponsiveness } from '@/hooks/useResponsiveness';

import { ImagesModal, ImagesModalRef } from './ImagesModal';

const imageSizes = {
  main: {
    height: 750,
    width: 1125,
  },
} as const;

export type ImagesGridProps = {
  store: AdaptedStore<true, AdaptedNetwork>;
  content: StoreProfilePageContent;
};

export function ImagesGrid({ store, content }: ImagesGridProps) {
  const { titleImage, logoImage, photos = [] } = store;

  const { isMobile } = useResponsiveness();

  const imageModalRef = useRef<ImagesModalRef>(null);

  const { profile } = content;

  const hasMore = photos.length > 2;

  const firstImagePreview = photos[0] ?? null;
  const secondImagePreview = photos[1] ?? null;

  const hasLogoImage = Boolean(logoImage?.url);

  function getThumbnailUrl(imageUrl: string) {
    const url = new URL(imageUrl);

    const height = isMobile
      ? imageSizes.main.height / 2
      : imageSizes.main.height;
    const width = isMobile ? imageSizes.main.width / 2 : imageSizes.main.width;

    url.searchParams.set('height', String(height));
    url.searchParams.set('width', String(width));
    url.searchParams.set('mode', 'fit');

    return url.href;
  }

  function onOpenModal(index: number) {
    return () => {
      imageModalRef.current?.open(index);
    };
  }

  const images = [
    titleImage,
    firstImagePreview,
    secondImagePreview,
    ...photos,
  ].filter(image => Boolean(image?.url)) as ZmyleImage[];

  if (!titleImage) return null;

  return (
    <>
      <ImagesModal ref={imageModalRef} images={images} />

      <div className="grid grid-cols-2 gap-8 lg:grid-cols-[65%_30%] lg:grid-rows-2">
        <motion.div
          className="relative col-span-2 flex h-full cursor-pointer lg:col-span-1 lg:row-span-2"
          whileTap={{ scale: 0.95 }}
          onClick={onOpenModal(0)}
          role="button"
        >
          <Image
            src={getThumbnailUrl(titleImage.url)}
            className="aspect-[3/2] rounded-md object-cover shadow-lg"
            alt={store.name}
            height={imageSizes.main.height}
            width={imageSizes.main.width}
          />

          {hasLogoImage && logoImage?.url && (
            <div className="absolute bottom-5 left-5 aspect-square w-[17.5%] min-w-24 rounded-md border-[5px] border-white bg-white">
              <Image
                src={logoImage.url}
                className="size-full rounded-md object-cover"
                alt={`${store.name} Logo`}
                width={100}
                height={100}
              />
            </div>
          )}
        </motion.div>

        <div>
          {firstImagePreview ? (
            <motion.div
              className="relative aspect-[3/2] cursor-pointer"
              onClick={onOpenModal(1)}
              whileTap={{ scale: 0.95 }}
              role="button"
            >
              <Image
                src={getThumbnailUrl(firstImagePreview.url)}
                className="rounded-md object-cover shadow-lg"
                alt="First store image"
                fill
              />
            </motion.div>
          ) : (
            <div className="bg-dynamic-base aspect-[3/2] size-full rounded-md shadow-lg" />
          )}
        </div>

        <div>
          {secondImagePreview ? (
            <div className="relative">
              <motion.div
                className="relative aspect-[3/2] cursor-pointer"
                whileTap={{ scale: 0.95 }}
                onClick={onOpenModal(2)}
                role="button"
              >
                <Image
                  src={getThumbnailUrl(secondImagePreview.url)}
                  className="rounded-md object-cover shadow-lg"
                  alt="Second store image"
                  fill
                />
              </motion.div>

              {hasMore && (
                <Button
                  variant="outline"
                  className={twJoin(
                    'absolute right-1/2 bottom-2 translate-x-1/2 md:right-5 md:bottom-4 md:translate-0 lg:bottom-5 lg:translate-x-0',
                    'rounded-md bg-white px-2 py-2 text-xs whitespace-nowrap md:px-4',
                  )}
                  onClick={onOpenModal(0)}
                >
                  {profile.showAllPicturesButton}
                </Button>
              )}
            </div>
          ) : (
            <div className="bg-dynamic-base aspect-[3/2] size-full rounded-md shadow-lg" />
          )}
        </div>
      </div>
    </>
  );
}
